import { useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Card } from "primereact/card";
import { confirmDialog } from "primereact/confirmdialog";
import { Tag } from "primereact/tag";
import { FileUpload } from "primereact/fileupload";

import { useAppDispatch } from "../../common/hooks/useStore";
import {
  createDepartment,
  deleteDepartment,
  exportDepartament,
  fetchDepartments,
  importDepartment,
  selectAllDepartments,
  updateDepartment,
} from "../departments/departmentsSlice";
import { Department } from "../departments/Department";
import DepartmentForm from "./DepartmentForm";
import NoResultsFound from "../../common/components/noResultsFound/NoResultsFound";

export default function DepartmentsPage() {
  const dispatch = useAppDispatch();
  const departments = useSelector(selectAllDepartments);
  const importDepartmentFileUploadRef = useRef<FileUpload>(null);
  const [selectedDepartment, setSelectedDepartment] = useState<Department | undefined>(undefined);
  const [showDepartmentDialog, setShowDepartmentDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [exportingDepartmentId, setExportingDepartmentId] = useState<string | undefined>(undefined);

  const closeDialog = () => {
    setSelectedDepartment(undefined);
    setShowDepartmentDialog(false);
  };

  const onCreate = () => {
    setSelectedDepartment(undefined);
    setShowDepartmentDialog(true);
    setIsEditing(false);
  };

  const onUpload = (event: any) => {
    confirmDialog({
      header: "Import departament",
      icon: "pi pi-exclamation-triangle",
      message: (
        <div className="text-xl">Doresti să muti toti utilizatorii din departamentul ce s-a exportat în departamentul importat?</div>
      ),
      acceptLabel: "Da",
      rejectLabel: "Nu",
      accept: async () => {
        await dispatch(importDepartment({ file: event.files[0], importUsers: true }));
        await dispatch(fetchDepartments());
        importDepartmentFileUploadRef.current?.clear();
      },

      reject: async () => {
        await dispatch(importDepartment({ file: event.files[0], importUsers: false }));
        await dispatch(fetchDepartments());
        importDepartmentFileUploadRef.current?.clear();
      },
    });
  };

  const onEdit = (department: Department) => {
    setSelectedDepartment(department);
    setShowDepartmentDialog(true);
    setIsEditing(true);
  };

  const onExport = async (department: Department) => {
    setExportingDepartmentId(department.id);
    await dispatch(exportDepartament(department));
    setExportingDepartmentId(undefined);
  };

  const onDelete = (department: Department) => {
    confirmDialog({
      header: "Confirmare stergere",
      icon: "pi pi-exclamation-triangle",
      message: (
        <div className="text-xl">
          Esti sigur ca vrei sa stergi departamentul <span className="font-bold">{department.name}</span>?
        </div>
      ),
      acceptLabel: "Da",
      rejectLabel: "Nu",
      accept: () => dispatch(deleteDepartment(department)),
    });
  };

  const onSubmit = async (department: Department) => {
    if (isEditing) {
      await dispatch(updateDepartment(department));
    } else {
      await dispatch(createDepartment(department));
    }
    closeDialog();
  };

  return (
    <Card
      title={
        <div className="flex align-items-start md:align-items-center justify-content-between">
          <div className="text-2xl font-bold">Departamente</div>
          <div className="hidden md:flex align-items-center gap-2">
            <FileUpload
              auto
              customUpload
              mode="basic"
              accept=".json"
              chooseLabel="Import"
              ref={importDepartmentFileUploadRef}
              uploadHandler={onUpload}
            ></FileUpload>
            <Button label="Adauga" icon="pi pi-plus" onClick={() => onCreate()} />
          </div>
        </div>
      }
    >
      <DataTable size="small" value={departments} emptyMessage={<NoResultsFound />}>
        <Column
          className="w-5rem"
          field="isActive"
          body={(rowData: Department) => {
            return rowData.isActive ? <Tag severity="success">Activ</Tag> : <Tag severity="danger">Inactiv</Tag>;
          }}
        ></Column>
        <Column field="name" header="Nume"></Column>
        <Column
          className="w-17rem"
          body={(rowData: Department) => (
            <div className="flex align-items-center justify-content-end gap-2">
              <Button
                className="hidden md:flex"
                size="small"
                label="Export"
                severity="help"
                icon="pi pi-download"
                onClick={() => onExport(rowData)}
                loading={exportingDepartmentId === rowData.id}
              ></Button>
              <Button size="small" label="Editeaza" icon="pi pi-pencil" onClick={() => onEdit(rowData)} />
              <Button size="small" label="Sterge" severity="danger" icon="pi pi-trash" onClick={() => onDelete(rowData)} />
            </div>
          )}
        ></Column>
      </DataTable>
      <Dialog
        className="w-almost-full md:w-6"
        header={selectedDepartment ? "Editeaza departament" : "Adauga departament"}
        visible={showDepartmentDialog}
        onHide={() => closeDialog()}
      >
        <DepartmentForm department={selectedDepartment} onSubmit={onSubmit} />
      </Dialog>
    </Card>
  );
}
